import { useState, useEffect } from "react";
import GoodbyePage from "./GoodbyePage";

export default function HoneyReturnWall({ user, onCancel, API_BASE }) {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [reactivating, setReactivating] = useState(false);

  const G = "#0077B5";
  const planName = user?.last_paid_plan
    ? user.last_paid_plan.charAt(0).toUpperCase() + user.last_paid_plan.slice(1)
    : "Paid";
  const planPrice = user?.last_paid_plan_price ?? null;
  const postsPerWeek = user?.last_paid_plan_posts_per_week ?? null;
  const avgImpressions = user?.last_paid_plan_avg_impressions ?? 37.6;
  const monthlyImpressions = postsPerWeek
    ? Math.round(postsPerWeek * 4 * avgImpressions)
    : null;
  const followerCount = user?.follower_count ?? null;
  const [lastPostDate, setLastPostDate] = useState(null);
  const [daysSilent, setDaysSilent] = useState(null);
  const [honeyPostCount, setHoneyPostCount] = useState(0);

  useEffect(() => {
    if (!API_BASE || !user?.id) return;
    fetch(`${API_BASE}/app/api/user/honey-wall-stats`, { credentials: "include" })
      .then(r => r.ok ? r.json() : {})
      .then(d => {
        setLastPostDate(d.last_post_date || null);
        setDaysSilent(d.days_since_last_post ?? null);
        setHoneyPostCount(d.honey_post_count ?? 0);
      })
      .catch(() => {});
  }, [API_BASE, user?.id]);

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/billing/portal-session`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Session-Token": localStorage.getItem("nectar_st"),
        },
      });
      const data = await r.json();
      if (r.ok && data.portal_url) {
        window.location.href = data.portal_url;
      } else {
        setReactivating(false);
      }
    } catch {
      setReactivating(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    setCancelError(null);
    try {
      const r = await fetch(`${API_BASE}/app/api/user/cancel-account`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Session-Token": localStorage.getItem("nectar_st"),
        },
        body: JSON.stringify({ action: "soft_delete" }),
      });
      if (r.ok) {
        setCancelled(true);
      } else {
        const data = await r.json().catch(() => ({}));
        setCancelError(data.error || "Something went wrong. Please try again.");
        setCancelling(false);
      }
    } catch {
      setCancelError("Network error. Please try again.");
      setCancelling(false);
    }
  };

  if (cancelled) {
    return <GoodbyePage />;
  }

  const style = {
    wrap: { background: "#F3F2EF", minHeight: "100vh", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" },
    inner: { maxWidth: 560, margin: "0 auto", padding: "48px 24px 80px" },
    logo: { fontFamily: "'Playfair Display',Georgia,serif", fontSize: 24, fontWeight: 700, color: G, display: "block", marginBottom: 40, textDecoration: "none" },
    amber: { background: "#FFFBEB", border: "1px solid #FCD34D", borderRadius: 10, padding: "14px 20px", color: "#92400E", fontSize: 14, marginBottom: 28 },
    card: { background: "#fff", border: "1px solid #E1E9EE", borderRadius: 12, padding: "24px 28px", marginBottom: 24 },
    cardLabel: { fontSize: 11, color: "#aaa", letterSpacing: "0.1em", fontFamily: "monospace", marginBottom: 16 },
    metric: { display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 },
    metricLabel: { fontSize: 14, color: "#555" },
    metricValue: { fontSize: 18, fontWeight: 700, color: "#1A1A1A" },
    silence: { fontSize: 14, color: "#B45309", marginBottom: 24, lineHeight: 1.6 },
    honeyNote: { fontSize: 13, color: "#888", marginBottom: 28, lineHeight: 1.6 },
    cta: { width: "100%", background: "#22C55E", color: "#fff", border: "none", borderRadius: 10, padding: "17px", fontSize: 17, fontFamily: "'Playfair Display',Georgia,serif", cursor: "pointer", marginBottom: 8 },
    ctaSub: { textAlign: "center", fontSize: 12, color: "#888", marginBottom: 32 },
    cancelLink: { textAlign: "center" },
    cancelBtn: { background: "none", border: "none", color: "#aaa", fontSize: 13, cursor: "pointer", textDecoration: "underline" },
    confirmBox: { background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "20px 24px", marginTop: 16 },
    confirmText: { fontSize: 14, color: "#7F1D1D", marginBottom: 16, lineHeight: 1.6 },
    btnRow: { display: "flex", gap: 12 },
    btnCancel: { flex: 1, background: "#EF4444", color: "#fff", border: "none", borderRadius: 8, padding: "12px", fontSize: 14, cursor: cancelling ? "not-allowed" : "pointer", opacity: cancelling ? 0.7 : 1 },
    btnKeep: { flex: 1, background: "transparent", color: "#374151", border: "1px solid #D1D5DB", borderRadius: 8, padding: "12px", fontSize: 14, cursor: "pointer" },
  };

  return (
    <div style={style.wrap}>
      <div style={style.inner}>
        <a href="https://brandhives.com" style={style.logo}>BrandHives</a>

        {/* Status banner */}
        <div style={style.amber}>
          Your <strong>{planName}</strong> plan is currently paused.
        </div>

        {/* What they had */}
        <div style={style.card}>
          <div style={style.cardLabel}>WHEN YOUR HIVE WAS ACTIVE</div>
          {postsPerWeek && (
            <div style={style.metric}>
              <span style={style.metricLabel}>Posts per week</span>
              <span style={style.metricValue}>{postsPerWeek}</span>
            </div>
          )}
          {monthlyImpressions && (
            <div style={style.metric}>
              <span style={style.metricLabel}>Est. monthly impressions</span>
              <span style={style.metricValue}>~{monthlyImpressions.toLocaleString()}</span>
            </div>
          )}
          {followerCount != null && (
            <div style={style.metric}>
              <span style={style.metricLabel}>LinkedIn followers reached</span>
              <span style={style.metricValue}>{followerCount.toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* What they're missing */}
        {daysSilent != null && lastPostDate && (
          <div style={style.silence}>
            Your network hasn't heard from you since <strong>{new Date(lastPostDate).toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" })}</strong>. That's <strong>{daysSilent} days</strong> of silence.
          </div>
        )}

        {/* Honey post notice */}
        {honeyPostCount > 0 && (
          <div style={style.honeyNote}>
            We've been sending one post per week on Wednesdays to keep your name out there while your plan is paused.
          </div>
        )}

        {/* Primary CTA */}
        <button
          style={{ ...style.cta, opacity: reactivating ? 0.7 : 1, cursor: reactivating ? "not-allowed" : "pointer" }}
          onClick={handleReactivate}
          disabled={reactivating}
        >
          {reactivating ? "Opening billing\u2026" : `Reactivate ${planName}${planPrice != null ? ` — $${planPrice}/mo` : ""}`}
        </button>
        <div style={style.ctaSub}>Your plan resumes immediately. All your settings and history are saved.</div>

        {/* Secondary: cancel */}
        <div style={style.cancelLink}>
          {!showCancelConfirm && (
            <button style={style.cancelBtn} onClick={() => setShowCancelConfirm(true)}>
              Cancel my account instead
            </button>
          )}
          {showCancelConfirm && (
            <div style={style.confirmBox}>
              <div style={style.confirmText}>
                Are you sure? This will permanently stop all posts and cancel your account.
              </div>
              {cancelError && <div style={{ fontSize: 13, color: "#B91C1C", marginBottom: 12 }}>{cancelError}</div>}
              <div style={style.btnRow}>
                <button style={style.btnCancel} onClick={handleCancel} disabled={cancelling}>
                  {cancelling ? "Cancelling…" : "Yes, cancel my account"}
                </button>
                <button style={style.btnKeep} onClick={() => setShowCancelConfirm(false)}>
                  Keep my account
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
